import { readCookie } from "common/utils/cookie";
import { track } from "common/utils/mixpanel/tracker";
import UserContextHelper from "components/jiffy/masthead-redesign2025/local-delivery/utils/user-context-helper";

const SELECTORS = {
  TOGGLE: ".js-local-delivery-toggle",
  ADDRESS: ".js-local-delivery-address",
  LIMITED_MESSAGE: ".js-limited-delivery-message"
};

const EMPTY_ADDRESS_MESSAGE = "Select Your Address";

const MIXPANEL_EVENTS = {
  WARNING_BANNER_CLICKED: "yellow_warning_banner_clicked"
};

/**
 * @class LocalDeliveryToggleClass
 *
 * Manages the local delivery toggle display in the masthead.
 * Reads address and eligibility from cookies and updates the UI accordingly.
 *
 * @example
 * const toggle = new LocalDeliveryToggleClass();
 */
export default class LocalDeliveryToggleClass {
  #address;

  #hasFullAddress = false;

  constructor() {
    this.init();
    this.#bindEvents();
  }

  /**
   * Gets the toggle DOM element
   * @private
   */
  // eslint-disable-next-line class-methods-use-this
  get #$toggle() {
    return document.querySelector(SELECTORS.TOGGLE);
  }

  /**
   * Gets the address display DOM element
   * @private
   */
  get #$address() {
    return this.#$toggle?.querySelector(SELECTORS.ADDRESS);
  }

  /**
   * Gets the limited delivery message DOM element
   * @private
   */
  // eslint-disable-next-line class-methods-use-this
  get #$limitedMessage() {
    return document.querySelector(SELECTORS.LIMITED_MESSAGE);
  }

  /**
   * Binds event listeners to the toggle
   * @private
   */
  #bindEvents() {
    this.#$toggle?.addEventListener("click", () => {
      // Track warning banner click if toggle is in warning state
      if (this.#$toggle.dataset.warning === "true") {
        this.#trackWarningBannerClicked();
      }
    });
  }

  /**
   * Tracks when user clicks the warning banner to update address
   * @private
   */
  // eslint-disable-next-line class-methods-use-this
  #trackWarningBannerClicked() {
    const zipcode = UserContextHelper.getSessionZipcode();

    track(MIXPANEL_EVENTS.WARNING_BANNER_CLICKED, {
      zipcode
    });
  }

  /**
   * Initialize the toggle by reading cookies and updating display
   * Can be called multiple times to refresh the display
   * @public
   */
  init() {
    if (!this.#$toggle || !this.#$address) return;

    this.#reflow();

    // Poll for cookies with smart retry logic to handle race conditions
    // during page navigation where cookies are set by after_action
    this.#pollForCompleteCookies();
  }

  /**
   * Reads cookies and updates all display elements
   * @private
   */
  #reflow() {
    this.#readCookies();
    this.#updateAddressDisplay();
    this.#updateWarningState();
  }

  /**
   * Polls for complete address cookies with exponential backoff
   * Stops when full address is found or max attempts reached
   * @param {number} attempt - Current attempt number
   * @param {number} maxAttempts - Maximum number of retry attempts
   * @private
   */
  #pollForCompleteCookies(attempt = 0, maxAttempts = 4) {
    if (attempt >= maxAttempts) return;

    const delay = Math.min(100 * 2 ** attempt, 1000); // 100ms, 200ms, 400ms, 800ms

    setTimeout(() => {
      const previousFullAddress = this.#hasFullAddress;
      const previousAddress = this.#address;

      const hasFullAddressCookie = readCookie("full_address") === "true";
      const currentAddress = readCookie("address");

      // Check if cookies have changed since last read
      const cookiesChanged =
        hasFullAddressCookie !== previousFullAddress || currentAddress !== previousAddress;

      // Update display if cookies changed
      if (cookiesChanged) {
        this.#reflow();
      }

      // Always continue polling through all attempts to catch late updates
      this.#pollForCompleteCookies(attempt + 1, maxAttempts);
    }, delay);
  }

  /**
   * Reads address and eligibility from cookies
   * @private
   */
  #readCookies() {
    let address = readCookie("address");

    if (!address) {
      this.#address = null;
      return;
    }

    // Decode URL encoding if present
    if (address.includes("%")) {
      address = decodeURIComponent(address);
    }

    // Replace + with spaces
    address = address.replace(/\+/g, " ");

    this.#address = address;

    // Check if user has provided full address (indicates eligibility)
    // The full_address cookie is set server-side when an eligible address is provided
    this.#hasFullAddress = readCookie("full_address") === "true";
  }

  /**
   * Updates the address display in the UI
   * @private
   */
  #updateAddressDisplay() {
    this.#$address.textContent = this.#address || EMPTY_ADDRESS_MESSAGE;
  }

  /**
   * Updates the warning state of the toggle
   * Shows warning if user has an address but doesn't have full address (not eligible)
   * @private
   */
  #updateWarningState() {
    const hasAddress = !!this.#address;
    const hasFullAddress = this.#hasFullAddress;
    const { localDeliveryAddressEligibility } = this.#$toggle.dataset;

    const showWarning = hasAddress && !hasFullAddress && localDeliveryAddressEligibility === "true";
    this.#renderWarningState(showWarning);
  }

  /**
   * Renders the warning state UI
   * @param {boolean} show - Whether to show the warning state
   * @private
   */
  #renderWarningState(show) {
    this.#$limitedMessage?.classList.toggle("hidden", !show);
    this.#$toggle.dataset.warning = show;
  }
}
